import {
  ComparisonsFragment,
  GetComparisonsQuery,
  UpdateCompareListMutation,
  UpdateCompareListMutationVariables,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { GET_COMPARISONS } from '@dustin-web/microsite-data/src/graphql/product/get-customer-product-lists.graphql'
import { UPDATE_COMPARE_LIST } from '@dustin-web/microsite-data/src/graphql/product/update-compare-list.graphql'
import {
  createProductListItem,
  createProductList,
} from '@dustin-web/microsite-data/src/utils/create-product-list'
import { useGraphql, useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'

export type ComparisonProductList = ComparisonsFragment['productLists']

export type UseComparisons = typeof useComparisons

export const useComparisons = () => {
  const { data: comparisonsData, error, mutate } = useGraphql<GetComparisonsQuery>(GET_COMPARISONS)

  const [updateCompareList] = useGraphqlMutation<
    UpdateCompareListMutation,
    UpdateCompareListMutationVariables
  >(UPDATE_COMPARE_LIST)

  if (error) {
    throw error
  }

  const updateComparisons = (
    comparisonData: ComparisonProductList,
    productId: string,
    categoryName: string,
    displayName: string
  ) => {
    const comparison = comparisonData?.find(item =>
      item?.items?.some(i => i?.productErpIdentifier === productId)
    )

    const itemExist = !!comparison

    return mutate(
      async () => {
        const newData = await updateCompareList({
          variables: {
            productErpIdentifier: productId,
            quantity: itemExist ? 0 : 1,
            productListName: comparison?.productListName,
          },
        })

        const oldProductList = comparisonsData?.comparisons?.productLists

        const productListId = newData?.updateCompareListWithProduct?.productListId
        const productListItemId = newData?.updateCompareListWithProduct?.productListItemId

        let updatedProductList

        if (itemExist) {
          // Remove
          const currentProductList = comparisonData?.find(listItem => {
            return listItem?.items?.some(
              productItem => productItem?.productErpIdentifier === productId
            )
          })

          const currentProductListItem = currentProductList?.items?.find(productItem => {
            return productItem?.productErpIdentifier === productId
          })

          updatedProductList = oldProductList
            ?.map(listItem => {
              const filteredItems = listItem?.items?.filter(
                item => item?.id !== currentProductListItem?.id
              )
              return { ...listItem, items: filteredItems }
            })
            .filter(item => item.items?.length)
        } else {
          // Add
          const category = oldProductList?.find(
            listItem => listItem?.productListName === categoryName
          )

          const newProductListItem = createProductListItem(
            productListItemId,
            productId,
            displayName
          )

          if (category) {
            const updatedCategory = {
              ...category,
              items: category.items
                ? [...category.items, newProductListItem]
                : [newProductListItem],
            }

            updatedProductList = oldProductList
              ? [
                  ...oldProductList.filter(
                    list => list?.productListName !== updatedCategory.productListName
                  ),
                  updatedCategory,
                ]
              : [updatedCategory]
          } else {
            // Add new category
            const newProductList = createProductList(
              productListId,
              categoryName,
              productListItemId,
              productId,
              displayName
            )

            updatedProductList = oldProductList
              ? [...oldProductList, newProductList]
              : [newProductList]
          }
        }

        const updatedData = {
          comparisons: { __typename: 'Comparisons', productLists: updatedProductList },
        }

        return updatedData as GetComparisonsQuery
      },
      {
        revalidate: false,
        rollbackOnError: true,
      }
    )
  }

  return {
    loading: !comparisonsData,
    error,
    comparisonsData: comparisonsData?.comparisons?.productLists ?? [],
    updateComparisons,
  }
}
